/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState } from 'react';
import { Popper } from '@material-ui/core';
import styled from '@emotion/styled';
import BounceLoader from '@comp/PlusCourseCard/BounceLoader';
import { Fonts, Spacings } from '@styles/index';
import ArrowLeft from '@icon/notes/ArrowLeft';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import ChevronLeft from '@icon/notes/ChevronLeft';
import { EpubReaderProps } from '../types';
import EpubView from '../epubView';

interface DisabledProps {
  $disabled: boolean;
}

const Wrapper = styled.div`
  overflow: hidden;
  height: 100%;
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
`;

const InnerWrapper = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  width: 100%;
  background-color: var(--color-base-1);
  transition: all 0.3s ease;
  filter: drop-shadow(0px 16px 32px rgba(233, 238, 242, 0.4));
`;

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;

const Loader = styled.div`
  top: 50%;
  left: 10%;
  right: 10%;
  margin-top: -0.5em;
  position: absolute;
  text-align: center;
`;

const NavigationWrapper = styled.div`
  height: 21px;
  background: var(--color-base-1);
  border: ${Spacings.SPACING_1} solid var(--color-divider-secondary);
  border-radius: ${Spacings.SPACING_1B};
  text-transform: uppercase;
  color: var(--color-text-primary);
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.P2};
  line-height: ${Fonts.ULTRA_SMALL};
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: ${Spacings.SPACING_6};
  padding-left: ${Spacings.SPACING_6};
`;

const NavigationButton = styled.div<DisabledProps>`
  position: absolute;
  top: 45%;
  z-index: 20;
  height: 43.2px;
  width: 43.2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-base-1);
  border: 1.2px solid var(--color-divider);
  box-shadow: ${boxShadowGenerator({
    xOffset: '0px',
    yOffset: '19.2px',
    blur: '38.4px'
  })};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  &:hover {
    background-color: ${({ $disabled }) =>
      $disabled ? 'var(--color-base-1)' : 'var(--color-base-light-0)'};
  }
`;

const StyKeyboardLeftArrow = styled(NavigationButton)`
  left: -21.6px;
`;

const StyKeyboardRightArrow = styled(NavigationButton)`
  right: -21.6px;
`;

const StyPopper = styled(Popper)`
  margin-top: ${Spacings.SPACING_2B};
  z-index: 10;
`;

const StyArrowLeft = styled(ArrowLeft)`
  margin-left: ${Spacings.SPACING_1B};
`;

const StyArrowRight = styled(ArrowLeft)`
  margin-left: ${Spacings.SPACING_1B};
  transform: rotateY(180deg);
`;

const ChevronRight = styled(ChevronLeft)`
  transform: rotateY(180deg);
`;

const START_OF_BOOK = 'START_OF_BOOK';
const END_OF_BOOK = 'END_OF_BOOK';
const FORWARD_DIRECTION = 'FORWARD';
const BACKWARD_DIRECTION = 'BACKWARD';

const EpubReader: React.FC<EpubReaderProps> = ({
  loadingView = (
    <Loader>
      <BounceLoader />
    </Loader>
  ),
  locationChanged = null,
  tocChanged = null,
  styles,
  title,
  swipeable,
  epubViewStyles,
  onNavigation,
  ...rest
}) => {
  const [disabled, setDisabled] = useState(null);
  const [toc, setToc] = useState([]);
  const [navigationDirection, setNavigationDirection] = useState('');
  const [leftNavPopperOpen, setLeftNavPopperOpen] = useState(false);
  const [rightNavPopperOpen, setRightNavPopperOpen] = useState(false);
  const [popupAnchorEl, setPopupAnchorEl] = useState(null);

  const readerRef = React.createRef<any>();

  const onTocChange = (toc) => {
    setToc(toc);
    if (tocChanged) tocChanged(toc);
  };

  const next = () => {
    const node = readerRef.current;
    node.nextPage();
    setNavigationDirection(FORWARD_DIRECTION);
    onRightNavigationLeave();
  };

  const prev = () => {
    const node = readerRef.current;
    node.prevPage();
    setNavigationDirection(BACKWARD_DIRECTION);
    onLeftNavigationLeave();
  };

  const onLeftNavigationHover = (e) => {
    if (disabled !== START_OF_BOOK) {
      setLeftNavPopperOpen(true);
      setPopupAnchorEl(e.target);
    }
  };
  const onLeftNavigationLeave = () => {
    setLeftNavPopperOpen(false);
    setPopupAnchorEl(null);
  };

  const onRightNavigationHover = (e) => {
    if (disabled !== END_OF_BOOK) {
      setRightNavPopperOpen(true);
      setPopupAnchorEl(e.target);
    }
  };
  const onRightNavigationLeave = () => {
    setRightNavPopperOpen(false);
    setPopupAnchorEl(null);
  };

  const onNavigationChange = ({
    isStartOfBook,
    isEndOfBook,
    currentChapter,
    pageNoInChapter
  }) => {
    let udpatedDisabledState = disabled;

    if (isStartOfBook) {
      udpatedDisabledState = START_OF_BOOK;
    } else if (isEndOfBook) {
      udpatedDisabledState = END_OF_BOOK;
    } else {
      udpatedDisabledState = null;
    }

    setDisabled(udpatedDisabledState);

    if (onNavigation && navigationDirection)
      onNavigation({
        currentChapter,
        pageNoInChapter,
        direction: navigationDirection,
        isStartOfBook: disabled === START_OF_BOOK,
        isEndOfBook: disabled === END_OF_BOOK
      });
  };

  return (
    <Wrapper>
      <InnerWrapper>
        <Container>
          <EpubView
            ref={readerRef}
            loadingView={loadingView}
            styles={epubViewStyles}
            {...rest}
            tocChanged={onTocChange}
            locationChanged={locationChanged}
            onNavigation={onNavigationChange}
          />
        </Container>
      </InnerWrapper>
      <StyKeyboardLeftArrow
        onClick={prev}
        onMouseEnter={onLeftNavigationHover}
        onMouseLeave={onLeftNavigationLeave}
        $disabled={disabled === START_OF_BOOK}
      >
        <ChevronLeft
          color={
            disabled === START_OF_BOOK
              ? 'var(--color-divider)'
              : 'var(--color-text-primary)'
          }
          // @ts-ignore
          height={Spacings.SPACING_6B}
          width={Spacings.SPACING_6B}
        />
      </StyKeyboardLeftArrow>
      <StyKeyboardRightArrow
        onClick={next}
        onMouseEnter={onRightNavigationHover}
        onMouseLeave={onRightNavigationLeave}
        $disabled={disabled === END_OF_BOOK}
      >
        <ChevronRight
          color={
            disabled === END_OF_BOOK
              ? 'var(--color-divider)'
              : 'var(--color-text-primary)'
          }
          // @ts-ignore
          height={Spacings.SPACING_6B}
          width={Spacings.SPACING_6B}
        />
      </StyKeyboardRightArrow>
      <StyPopper
        open={leftNavPopperOpen && disabled !== START_OF_BOOK}
        anchorEl={popupAnchorEl}
        placement="bottom"
      >
        <NavigationWrapper>
          PREVIOUS PAGE{' '}
          <StyArrowLeft
            width={Spacings.SPACING_4B}
            height={Spacings.SPACING_4B}
            color="var(--color-text-primary)"
          />
        </NavigationWrapper>
      </StyPopper>
      <StyPopper
        open={rightNavPopperOpen && disabled !== END_OF_BOOK}
        anchorEl={popupAnchorEl}
        placement="bottom"
      >
        <NavigationWrapper>
          NEXT PAGE{' '}
          <StyArrowRight
            width={Spacings.SPACING_4B}
            height={Spacings.SPACING_4B}
            color="var(--color-text-primary)"
          />
        </NavigationWrapper>
      </StyPopper>
    </Wrapper>
  );
};

export default EpubReader;
